/**
 * The month calendar — spec 01 §7's first UX requirement, for one subject.
 *
 * `GET /attendance?date_from=<1st>&date_to=min(<last>, today)&employee_id=<subject>`.
 *
 * ── Why one subject and not the whole team ──────────────────────────────────
 *
 * `GET /attendance` pages the `(employee × date)` grid **employee-major**:
 * `attendance-service.ts` computes `employeeOffset = offset / range.days`, so a
 * 31-day window at the endpoint's default limit of 50 is one whole employee and
 * nineteen days of a second one. A month grid for a team would therefore be showing
 * a page boundary in the middle of somebody's month and calling it a calendar.
 *
 * So the two reads on this screen are shaped so that each one's page boundary falls
 * somewhere a reader can be told about: this one is **one employee, one month**
 * (limit = the number of days), and `RosterList` is **one day, many employees**.
 *
 * ── Why a day after today is not drawn ──────────────────────────────────────
 *
 * Because `GET /attendance` would answer for it. `resolveDateRange` refuses only an
 * inverted or over-long range, and every cell in the window comes back — a future
 * one as `data_incomplete` / `no_session_row`, since no session row exists for a day
 * that has not happened. Rendering that puts "Thiếu dữ liệu" on next Tuesday, which
 * is a hole in the data invented out of the calendar. `monthRange` clamps the read
 * and `monthGrid` marks the rest `future`: no badge, no button, and no request.
 *
 * ── Why a cell can still say "no cell" ──────────────────────────────────────
 *
 * The grid is dense — a `(employee, date)` pair with no session row is **on the
 * page** rather than missing from it — so every day in the window should have
 * exactly one cell. `k3.month.noCell` is what renders if one does not: a truncated
 * page, or a subject the server narrowed away. Silently drawing a blank there would
 * be indistinguishable from a day with nothing on it.
 */

import { useMemo, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatDay, formatMonth } from '../../i18n/datetime';
import { attendanceGridPath } from '../../api/paths';
import type { AttendanceGridDay, AttendanceGridResponse } from '../../api/types';
import { useApiQuery } from '../../api/useApiQuery';
import {
  dayCount,
  isoWeekdayOf,
  monthGrid,
  monthOf,
  monthRange,
  shiftMonth,
  type Month,
} from '../../attendance/month';
import { newestEventAt } from '../../attendance/status';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { DataDelayed } from '../../states/DataDelayed';
import { EmptyState } from '../../states/EmptyState';
import { Skeleton } from '../../states/Skeleton';
import { StatusBadge } from './StatusBadge';

/** Monday first: the audience writes the week T2…CN, and the labels come from the catalogue. */
const WEEKDAY_KEYS = [
  'k3.weekday.1',
  'k3.weekday.2',
  'k3.weekday.3',
  'k3.weekday.4',
  'k3.weekday.5',
  'k3.weekday.6',
  'k3.weekday.7',
] as const;

export interface MonthCalendarProps {
  readonly month: Month;
  /** Local date "today" in the **company's** zone, resolved once by the screen. */
  readonly today: string;
  readonly timezone: string;
  /** `null` when no subject has been chosen — the calendar asks for one. */
  readonly employeeId: string | null;
  readonly employeeName: string | null;
  readonly onOpenDay: (employeeId: string, date: string) => void;
  readonly onMonthChange: (month: Month) => void;
  readonly reloadToken?: number;
}

export function MonthCalendar({
  month,
  today,
  timezone,
  employeeId,
  employeeName,
  onOpenDay,
  onMonthChange,
  reloadToken = 0,
}: MonthCalendarProps): ReactNode {
  const { t, locale } = useI18n();
  const range = monthRange(month, today);
  const path =
    employeeId === null || range === null
      ? null
      : attendanceGridPath({
          from: range.from,
          to: range.to,
          limit: dayCount(range),
          employeeId,
        });
  const { status, data, error, reload } = useApiQuery<AttendanceGridResponse>(path, reloadToken);

  const byDate = useMemo(() => {
    const map = new Map<string, AttendanceGridDay>();
    for (const day of data?.data ?? []) map.set(day.date, day);
    return map;
  }, [data]);

  const weeks = monthGrid(month, today);
  const unsynced = (data?.data ?? []).filter((day) => day.completeness.unsynced_data);

  // Which of the seven columns is today's, or `null` when the month on screen is
  // not the month today is in. The grid is Monday-first, so the ISO weekday is the
  // column index plus one; the cell itself is marked by date, and this only tells
  // the eye which column to run down.
  const todayColumn = monthOf(today) === month ? isoWeekdayOf(today) - 1 : null;

  const monthLabel = formatMonth(month, locale);
  const heading =
    employeeName === null
      ? t('k3.month.heading', { month: monthLabel })
      : t('k3.month.of', { month: monthLabel, employee: employeeName });

  return (
    <section className="wp-section" aria-labelledby="k3-month-heading">
      <div className="wp-screen-head">
        <h2 className="wp-section__title" id="k3-month-heading">
          {heading}
        </h2>
        <div className="wp-daypicker">
          <button
            type="button"
            className="wp-button"
            onClick={() => {
              onMonthChange(shiftMonth(month, -1));
            }}
            data-testid="k3-month-prev"
          >
            {t('k3.month.prev')}
          </button>
          <button
            type="button"
            className="wp-button"
            onClick={() => {
              onMonthChange(shiftMonth(month, 1));
            }}
            data-testid="k3-month-next"
          >
            {t('k3.month.next')}
          </button>
        </div>
      </div>

      {employeeId === null ? (
        <EmptyState title={t('k3.month.pickEmployee')} testId="k3-month-no-subject" />
      ) : null}

      {employeeId !== null && range === null ? (
        <EmptyState title={t('k3.month.allFuture')} testId="k3-month-all-future" />
      ) : null}

      {path !== null && status === 'loading' ? (
        <Skeleton
          rows={5}
          variant="block"
          label={t('k3.month.loading')}
          testId="k3-month-skeleton"
        />
      ) : null}

      {path !== null && status === 'error' && error !== null ? (
        <ApiErrorNote
          error={error}
          onRetry={reload}
          subject={t('k3.month.subject')}
          testId="k3-month-error"
        />
      ) : null}

      {path !== null && status === 'ok' ? (
        <>
          <div className="wp-table-scroll">
            <table className="wp-b-cal" data-testid="k3-month-grid">
              <caption className="wp-visually-hidden">{heading}</caption>
              <thead>
                <tr>
                  {WEEKDAY_KEYS.map((key, column) => (
                    <th
                      key={key}
                      scope="col"
                      data-today={column === todayColumn ? 'true' : 'false'}
                    >
                      {t(key)}
                      {/* Today is named once, in its column head, and drawn once, as
                          a ring round its own cell. Printing the word inside the cell
                          as well costs the status its last twenty pixels in a fixed
                          seven-column table — and the status is the data. */}
                      {column === todayColumn ? (
                        <span className="wp-label wp-b-cal__mark">{t('k3.month.today')}</span>
                      ) : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weeks.map((week, weekIndex) => (
                  <tr key={week[0]?.date ?? `week-${String(weekIndex)}`}>
                    {week.map((cell, cellIndex) => {
                      if (cell.kind === 'pad' || cell.date === null) {
                        return (
                          <td
                            key={`pad-${String(weekIndex)}-${String(cellIndex)}`}
                            className="wp-b-cal__cell wp-b-cal__cell--pad"
                          />
                        );
                      }
                      const isToday = cell.date === today;
                      // The same word again, for the list of days the seven columns
                      // become below 900px — where the column head that carries it is
                      // gone and there is room in the line for it.
                      const mark = isToday ? (
                        <span className="wp-label wp-b-cal__mark">{t('k3.month.today')}</span>
                      ) : null;
                      // The weekday, carried in the cell as well as in the column
                      // head. Below 900px the seven columns become one list of days
                      // (`group-b.css`) — the head goes with them, and a day without
                      // its weekday is a date a reader has to count out.
                      const weekday = (
                        <span className="wp-b-cal__weekday">
                          {t(WEEKDAY_KEYS[cellIndex] ?? WEEKDAY_KEYS[0])}
                        </span>
                      );
                      if (cell.kind === 'future') {
                        // Not a button: there is nothing behind it to open, and a
                        // focusable control that does nothing is a keyboard user
                        // walking thirty squares to find out.
                        return (
                          <td
                            key={cell.date}
                            className="wp-b-cal__cell wp-b-cal__cell--future"
                            data-testid={`k3-cell-${cell.date}`}
                          >
                            <span className="wp-b-cal__inner">
                              {weekday}
                              <span className="wp-b-cal__day">{cell.dayOfMonth}</span>
                              <span className="wp-small wp-muted wp-b-cal__note">
                                {t('k3.month.future')}
                              </span>
                            </span>
                          </td>
                        );
                      }
                      const day = byDate.get(cell.date);
                      return (
                        <td
                          key={cell.date}
                          className={
                            isToday ? 'wp-b-cal__cell wp-b-cal__cell--today' : 'wp-b-cal__cell'
                          }
                          data-testid={`k3-cell-${cell.date}`}
                        >
                          {day === undefined ? (
                            <span className="wp-b-cal__inner">
                              {weekday}
                              <span className="wp-b-cal__day">{cell.dayOfMonth}</span>
                              <span className="wp-small wp-muted wp-b-cal__note">
                                {t('k3.month.noCell')}
                              </span>
                              {mark}
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="wp-b-cal__open wp-b-cal__inner"
                              onClick={() => {
                                onOpenDay(day.employee_id, day.date);
                              }}
                              aria-label={t('k3.month.open', {
                                date: formatDay(day.date, locale, {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                }),
                              })}
                              data-testid={`k3-open-${day.date}`}
                            >
                              {weekday}
                              <span className="wp-b-cal__day">{cell.dayOfMonth}</span>
                              <StatusBadge
                                status={day.attendance_status}
                                testId={`k3-badge-${day.date}`}
                              />
                              {mark}
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="wp-small wp-muted">{t('k3.month.futureNote')}</p>

          {unsynced.length === 0 ? null : (
            <DataDelayed
              source="processing"
              since={newestEventAt(unsynced)}
              timezone={timezone}
              detail={t('attendance.unsynced')}
              testId="k3-month-unsynced"
            />
          )}
        </>
      ) : null}
    </section>
  );
}
